import { supabase } from '../supabaseClient';

/**
 * 챌린지 인증 사진 기기 보관소(Cache Storage). 사진 경로는 업로드 후 바뀌지 않으므로 기간 없이 재사용한다.
 * 사용자별 보관소로 나누고, 용량 상한을 넘으면 오래 안 본 사진부터 지운다.
 */
const CACHE_PREFIX = 'cp-chl-photos-v1-';
const INDEX_PREFIX = 'cp.assignmentChallenge.photoIndex.v1.';
const MAX_BYTES = 50 * 1024 * 1024;
const KEY_ORIGIN = 'https://cp-chl-photo.invalid/';

/** b: 바이트, t: 마지막 사용 시각, m: 서버에 파일 없음(예전 사진의 썸네일 등) */
type IndexEntry = { b: number; t: number; m?: 1 };
type PhotoIndex = Record<string, IndexEntry>;

const objectUrls = new Map<string, string>();

export function photoDeviceCacheSupported(): boolean {
  return typeof window !== 'undefined' && typeof caches !== 'undefined' && window.isSecureContext;
}

async function currentUid(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? null;
}

function keyUrl(path: string): string {
  return `${KEY_ORIGIN}${encodeURIComponent(path)}`;
}

function readIndex(uid: string): PhotoIndex {
  try {
    const raw = window.localStorage.getItem(`${INDEX_PREFIX}${uid}`);
    return raw ? (JSON.parse(raw) as PhotoIndex) : {};
  } catch {
    return {};
  }
}

function writeIndex(uid: string, index: PhotoIndex): void {
  try {
    window.localStorage.setItem(`${INDEX_PREFIX}${uid}`, JSON.stringify(index));
  } catch {
    /* ignore quota */
  }
}

async function openStore(): Promise<{ uid: string; cache: Cache } | null> {
  if (!photoDeviceCacheSupported()) return null;
  const uid = await currentUid();
  if (!uid) return null;
  try {
    return { uid, cache: await caches.open(`${CACHE_PREFIX}${uid}`) };
  } catch {
    return null;
  }
}

/** 보관된 사진의 object URL. 없는 경로는 결과에서 빠진다 */
export async function readCachedPhotos(paths: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  const store = await openStore();
  if (!store) return out;
  const index = readIndex(store.uid);
  const now = Date.now();
  await Promise.all(
    Array.from(new Set(paths)).map(async (path) => {
      const memKey = `${store.uid}|${path}`;
      const mem = objectUrls.get(memKey);
      if (mem) {
        out[path] = mem;
      } else {
        const res = await store.cache.match(keyUrl(path)).catch(() => undefined);
        if (!res) return;
        const blob = await res.blob().catch(() => null);
        if (!blob || blob.size === 0) return;
        const url = URL.createObjectURL(blob);
        objectUrls.set(memKey, url);
        out[path] = url;
      }
      if (index[path]) index[path].t = now;
    })
  );
  writeIndex(store.uid, index);
  return out;
}

/** 내려받은 사진을 보관하고 object URL을 돌려준다 */
export async function storePhotos(entries: Array<{ path: string; blob: Blob }>): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  const store = await openStore();
  entries.forEach(({ path, blob }) => {
    const memKey = `${store?.uid ?? ''}|${path}`;
    const url = objectUrls.get(memKey) ?? URL.createObjectURL(blob);
    objectUrls.set(memKey, url);
    out[path] = url;
  });
  if (!store || entries.length === 0) return out;
  const index = readIndex(store.uid);
  const now = Date.now();
  await Promise.all(
    entries.map(async ({ path, blob }) => {
      try {
        await store.cache.put(
          keyUrl(path),
          new Response(blob, { headers: { 'content-type': blob.type || 'image/webp' } })
        );
        index[path] = { b: blob.size, t: now };
      } catch {
        /* 보관 실패는 무시(다음에 다시 내려받음) */
      }
    })
  );
  let total = Object.values(index).reduce((sum, e) => sum + e.b, 0);
  if (total > MAX_BYTES) {
    const oldest = Object.entries(index)
      .filter(([, e]) => !e.m)
      .sort((a, b) => a[1].t - b[1].t);
    for (const [path, e] of oldest) {
      if (total <= MAX_BYTES * 0.8) break;
      await store.cache.delete(keyUrl(path)).catch(() => false);
      delete index[path];
      total -= e.b;
    }
  }
  writeIndex(store.uid, index);
  return out;
}

export async function markPhotosMissing(paths: string[]): Promise<void> {
  const store = await openStore();
  if (!store || paths.length === 0) return;
  const index = readIndex(store.uid);
  const now = Date.now();
  paths.forEach((p) => {
    index[p] = { b: 0, t: now, m: 1 };
  });
  writeIndex(store.uid, index);
}

export async function listMissingPhotos(paths: string[]): Promise<Set<string>> {
  const store = await openStore();
  if (!store) return new Set();
  const index = readIndex(store.uid);
  return new Set(paths.filter((p) => index[p]?.m));
}

export async function deleteCachedPhotos(paths: string[]): Promise<void> {
  const store = await openStore();
  if (!store || paths.length === 0) return;
  const index = readIndex(store.uid);
  await Promise.all(
    paths.map(async (p) => {
      await store.cache.delete(keyUrl(p)).catch(() => false);
      delete index[p];
      const memKey = `${store.uid}|${p}`;
      const url = objectUrls.get(memKey);
      if (url) {
        URL.revokeObjectURL(url);
        objectUrls.delete(memKey);
      }
    })
  );
  writeIndex(store.uid, index);
}

export function clearChallengePhotoObjectUrls(): void {
  objectUrls.forEach((url) => URL.revokeObjectURL(url));
  objectUrls.clear();
}

/** 직접 로그아웃 시: 모든 사용자의 사진 보관소와 색인을 비운다 */
export async function clearAllChallengePhotoCaches(): Promise<void> {
  objectUrls.forEach((url) => URL.revokeObjectURL(url));
  objectUrls.clear();
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(INDEX_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  if (typeof caches === 'undefined') return;
  try {
    const names = await caches.keys();
    await Promise.all(names.filter((n) => n.startsWith(CACHE_PREFIX)).map((n) => caches.delete(n)));
  } catch {
    /* ignore */
  }
}
